import React, { useEffect, useRef, useState } from 'react';
import AgoraRTC from 'agora-rtc-sdk-ng';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { markCallAsReceived, setMeetingDetails } from '../../../../../redux/slices/meetingDetailsSlice';
import { GetMeetingDetails } from '../../../services/chat.services';
import Swal from 'sweetalert2';
import { SWAL_SETTINGS } from '../../../../../utils/Constants';

const VideoCall = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const meetingId = searchParams.get('meeting_id');
  const loggedInUser = JSON.parse(localStorage.getItem('psychic') || '{}');
  // const meetingDetails = useSelector(state => state.meetingDetailsSlice.meetingDetails);
  const meetingDetailsRef = useRef(null);
  meetingDetailsRef.current = useSelector(state => state.meetingDetailsSlice.meetingDetails);

  const client = useRef(null);
  const localAudioTrack = useRef(null);
  const localVideoTrack = useRef(null);

  const [micMuted, setMicMuted] = useState(false);
  const [videoOff, setVideoOff] = useState(false);
  const [speakerOn, setSpeakerOn] = useState(true);
  const [remoteMicMuted, setRemoteMicMuted] = useState(true);
  const [remoteVideoOff, setRemoteVideoOff] = useState(true);
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [joined, setJoined] = useState(false);
  const [timeLeftCounter, setTimeLeftCounter] = useState(1800);
  const [ringingDots, setRingingDots] = useState('');
  const [showControls, setShowControls] = useState(true);
  const [isLocalTracksCreated, setIsLocalTracksCreated] = useState(false);

  const hasRemoteUser = remoteUsers.length > 0;

  const [isVideoCall, setIsVideoCall] = useState(false);
  // const isVideoCall = meetingDetailsRef.current?.call_info?.meeting_type === 'video';
  // console.log('---------meeting details', meetingDetailsRef.current);

  const isPsychic = loggedInUser._id === meetingDetailsRef.current?.call_info?.psychic_id;

  const remoteName = isPsychic
    ? meetingDetailsRef.current?.call_info?.customer_name || "Customer"
    : meetingDetailsRef.current?.call_info?.psychic_name || "Psychic";
  const remoteImage = isPsychic
    ? meetingDetailsRef.current?.call_info?.customer_profile_image
    : meetingDetailsRef.current?.call_info?.psychic_profile_image;
  const localImage = isPsychic
    ? meetingDetailsRef.current?.call_info?.psychic_profile_image
    : meetingDetailsRef.current?.call_info?.customer_profile_image;

  const generateUid = () => Math.floor(Math.random() * 99999) + 10000;

  // Auto-hide controls
  useEffect(() => {
    if (showControls) {
      const timer = setTimeout(() => setShowControls(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [showControls]);

  const showControlsTemporarily = () => setShowControls(true);

  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  // Real 1-hour countdown from meeting_date
  useEffect(() => {
    if (!meetingDetailsRef.current?.call_info?.meeting_date) return;

    const meetingStart = new Date(meetingDetailsRef.current?.call_info.meeting_date).getTime();
    const meetingEnd = meetingStart + 60 * 60 * 1000;

    const updateTimer = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((meetingEnd - now) / 1000));
      setTimeLeftCounter(remaining);

      if (remaining <= 0) {
        Swal.fire({
          icon: "info",
          title: "Session Ended",
          text: "Your 1-hour session has ended.",
          ...SWAL_SETTINGS
        }).then(() => navigate(-1));
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [  meetingDetailsRef.current?.call_info?.meeting_date, navigate]);

  // === INIT ===
  useEffect(() => {
    const init = async () => {
      await getMeetingDetails();
      await grantLocalDevicesPermissions();
    };
    init();
    return () => leaveCurrentPage();
  }, []);

  async function getMeetingDetails() {
    try {
      const { data } = await GetMeetingDetails({ meeting_id: meetingId });
      dispatch(setMeetingDetails({ call_info: { ...data, uid: generateUid() } }));
      // meetingDetailsRef.current = { call_info: { ...data, uid: generateUid() } };

      setIsVideoCall(data.meeting_type === 'video');
    } catch (err) {
      Swal.fire({ icon: "error", text: "Failed to load meeting", ...SWAL_SETTINGS });
      navigate(-1);
    }
  }

  async function grantLocalDevicesPermissions() {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true, video: isVideoCall });
      initializeLocalSetup();
    } catch {
      Swal.fire({ icon: "error", text: "Please allow camera & microphone access", ...SWAL_SETTINGS });
      navigate(-1);
    }
  }

  async function initializeLocalSetup() {
    await createLocalTracks();
    setIsLocalTracksCreated(true);
  }

  async function createLocalTracks() {
    try {
      console.log('Creating local tracks...');
      console.log(meetingDetailsRef.current);

      localAudioTrack.current = await AgoraRTC.createMicrophoneAudioTrack();


      if (meetingDetailsRef.current?.call_info?.meeting_type === 'video') {
        localVideoTrack.current = await AgoraRTC.createCameraVideoTrack({
          encoderConfig: "480p_1",
          facingMode: "user", // Forces front camera on mobile
        });

        console.log('Local video track created.', localVideoTrack.current);

        // Play local video immediately after creation
        setTimeout(() => {
          const container = document.getElementById("local-video-container");
          console.log('Playing local video track...', container, videoOff);
          if (container && !videoOff) {
            container.innerHTML = "";
            localVideoTrack.current.play(container, { fit: "cover", mirror: true });
          }
        }, 300);
      }
    } catch (err) {
      console.error("Failed to create tracks:", err);
      Swal.fire({ icon: "error", text: "Failed to access camera/microphone", ...SWAL_SETTINGS });
    }
  }

  // AUTO-PLAY LOCAL VIDEO WHEN TRACK EXISTS OR TOGGLED
  useEffect(() => {
    const container = document.getElementById("local-video-container");
    if (!container) return;

    if (localVideoTrack.current && !videoOff) {
      container.innerHTML = "";
      localVideoTrack.current.play(container, { fit: "cover", mirror: true });
    } else {
      container.innerHTML = "";
    }
  }, [localVideoTrack.current, videoOff]);

  async function joinChannel() {
    if (!localAudioTrack.current || joined) return;

    const { uid, channel } = meetingDetailsRef.current?.call_info;

    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/generate-agora-token?channel=${channel}&uid=${uid}`
      );

      client.current = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
      setupEventListeners();

      await client.current.join(process.env.REACT_APP_AGORA_APP_ID, channel, data.token, uid);
      client.current.enableAudioVolumeIndicator();

      // const tracks = [localAudioTrack.current];
      // if (localVideoTrack.current && !videoOff) tracks.push(localVideoTrack.current);
      // await client.current.publish(tracks);
      const tracksToPublish = [];

      // Always include audio
      if (localAudioTrack.current) {
        tracksToPublish.push(localAudioTrack.current);
      }

      // Only add video if it's on
      if (localVideoTrack.current && !videoOff) {
        tracksToPublish.push(localVideoTrack.current);
      }

      if (tracksToPublish.length > 0) {
        await client.current.publish(tracksToPublish);
      }

      setJoined(true);
      startRingingDots();
    } catch (err) {
      console.error("Join failed:", err);
      Swal.fire({ icon: "error", text: "Failed to join call", ...SWAL_SETTINGS });
    }
  }

  function setupEventListeners() {
    client.current.on("user-published", async (user, mediaType) => {
      await client.current.subscribe(user, mediaType);

      if (mediaType === "video") {
        setRemoteVideoOff(false);
        const container = document.getElementById("remote-video-container");
        if (container) {
          container.innerHTML = "";
          user.videoTrack.play(container, { fit: "contain", mirror: false });
        }
      }

      if (mediaType === "audio") {
        user.audioTrack.play();
        setRemoteMicMuted(false);
      }

      if (!hasRemoteUser) {
        setRemoteUsers([user]);
        endRingingDots();
        dispatch(markCallAsReceived());
      }
    });

    client.current.on("user-unpublished", (user, mediaType) => {
      if (mediaType === "video") setRemoteVideoOff(true);
      if (mediaType === "audio") setRemoteMicMuted(true);

      const container = document.getElementById("remote-video-container");
      if (container && mediaType === "video") container.innerHTML = "";
    });

    client.current.on("user-left", () => {
      setRemoteUsers([]);
      setRemoteVideoOff(true);
      setRemoteMicMuted(true);
      const container = document.getElementById("remote-video-container");
      if (container) container.innerHTML = "";
      startRingingDots();
    });
  }

  // const toggleMic = async () => {
  //   if (!localAudioTrack.current) return;
  //   await localAudioTrack.current.setEnabled(!localAudioTrack.current.enabled);
  //   setMicMuted(!localAudioTrack.current.enabled);
  // };
 const toggleMic = async () => {
    if (!localAudioTrack.current || !client.current || !joined) return;

    const willBeMuted = !micMuted;

    if (willBeMuted) {
      // MUTE: Just disable locally (DO NOT unpublish!)
      await localAudioTrack.current.setEnabled(false);
    } else {
      // UNMUTE: Enable and make sure it's published
      await localAudioTrack.current.setEnabled(true);
      
      // Only publish if not already published
      const publishedTracks = client.current.localTracks || [];
      const isPublished = publishedTracks.includes(localAudioTrack.current);
      
      if (!isPublished) {
        await client.current.publish([localAudioTrack.current]);
      }
    }

    setMicMuted(willBeMuted);
  };

 const toggleVideo = async () => {
     if (!localVideoTrack.current || !client.current || !joined) return;

     if (videoOff) {
         // TURN VIDEO ON
         try {
             await localVideoTrack.current.setEnabled(true);
             await client.current.publish([localVideoTrack.current]);
             setVideoOff(false);

             // Immediately play local preview
             const container = document.getElementById("local-video-container");
             if (container) {
                 container.innerHTML = "";
                 localVideoTrack.current.play(container, { fit: "cover", mirror: true });
             }
         } catch (err) {
             console.error("Failed to publish video:", err);
         }
     } else {
         // TURN VIDEO OFF
         try {
             await client.current.unpublish([localVideoTrack.current]);
             await localVideoTrack.current.setEnabled(false);
             setVideoOff(true);

             // Clear preview
             const container = document.getElementById("local-video-container");
             if (container) container.innerHTML = "";
         } catch (err) {
             console.error("Failed to unpublish video:", err);
         }
     }
 };

  const toggleSpeaker = () => {
      const newState = !speakerOn;
      setSpeakerOn(newState);

      // Safely try to switch speaker — ignore errors (common on desktop)
      const applySpeaker = async (audioTrack) => {
          if (!audioTrack || !audioTrack.setPlaybackDevice) return;

          try {
              // This will work on real Android/iOS phones
              // On desktop it will throw — we catch it
              await audioTrack.setPlaybackDevice(newState ? "speaker" : "earpiece");
          } catch (err) {
              // Expected on desktop browsers — completely safe to ignore
              console.log("Speaker toggle not supported on this device (normal on desktop)");
          }
      };

      // Apply to all remote users
      client.current?.remoteUsers.forEach((user) => {
          if (user.audioTrack) {
              applySpeaker(user.audioTrack);
          }
      });

      // Apply to local audio (some phones need it)
      if (localAudioTrack.current) {
          applySpeaker(localAudioTrack.current);
      }
  };

  async function leaveCurrentPage() {
    localAudioTrack.current?.close();
    localVideoTrack.current?.close();
    if (client.current) {
      await client.current.leave();
      client.current = null;
    }
    dispatch(setMeetingDetails(null));
  }

  function startRingingDots() {
    const int = setInterval(() => setRingingDots(p => p.length >= 3 ? '' : p + '.'), 600);
    return () => clearInterval(int);
  }

  function endRingingDots() {
    setRingingDots('');
  }

  return (
    <div
      className="vh-100 bg-black position-relative overflow-hidden"
      onClick={showControlsTemporarily}
      onMouseMove={showControlsTemporarily}
    >
      <div className="h-100 d-flex align-items-center justify-content-center p-3">
        <div className="position-relative" style={{ maxWidth: '560px', width: '100%', aspectRatio: '9/16' }}>

          {/* Remote Video + Profile */}
          {hasRemoteUser ? (
            <div className="w-100 h-100 position-relative rounded-4 overflow-hidden bg-dark shadow-2xl">
              <div id="remote-video-container" className="w-100 h-100" style={{ background: '#000' }} />

              {/* Remote Info (raised high) */}
              <div className="position-absolute start-0 end-0 p-4 text-white" style={{ bottom: '140px', background: 'linear-gradient(transparent, rgba(0,0,0,0.85))', zIndex: 10 }}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-3">
                    <img src={remoteImage || "/avatar.png"} alt={remoteName} className="rounded-circle border border-white border-3" style={{ width: 56, height: 56, objectFit: 'cover' }} />
                    <div>
                      <h5 className="mb-0 fw-bold fs-4">{remoteName}</h5>
                      <small className="text-success">Connected</small>
                    </div>
                  </div>
                  <div className="d-flex gap-3">
                    {remoteMicMuted && <i className="ti ti-microphone-off text-danger fs-2" />}
                    {remoteVideoOff && <i className="ti ti-video-off text-danger fs-2" />}
                  </div>
                </div>
              </div>

              {/* Remote Camera Off Overlay */}
              {remoteVideoOff && (
                <div className="position-absolute inset-0 d-flex flex-column align-items-center justify-content-center text-white z-10">
                  <img src={remoteImage || "/avatar.png"} alt={remoteName} className="rounded-circle border border-white border-5 shadow-2xl" style={{ width: 180, height: 180, objectFit: 'cover' }} />
                  <h2 className="mt-5 fw-bold fs-1">{remoteName}</h2>
                  <p className="text-muted fs-3">Camera is off</p>
                </div>
              )}
            </div>
          ) : joined ? (
            <div className="h-100 d-flex flex-column align-items-center justify-content-center text-white">
              <img src={remoteImage || "/avatar.png"} alt={remoteName} className="rounded-circle mb-4 shadow-lg" style={{ width: 140, height: 140 }} />
              <h3 className="fw-bold">{remoteName}</h3>
              <p className="fs-4">Waiting to join{ringingDots}</p>
            </div>
          ) : (
            <div className="text-center text-white">
              <h3 className="mb-5">Setting up your call...</h3>
              {isLocalTracksCreated && (
                <button onClick={joinChannel} className="btn btn-primary btn-lg px-5 py-3 rounded-pill shadow-lg">
                  Join Call
                </button>
              )}
            </div>
          )}

          {/* Timer */}
          {timeLeftCounter > 0 && (
            <div className="position-absolute top-4 start-4">
              <span className={`badge fs-3 px-5 py-3 rounded-pill shadow-2xl ${timeLeftCounter <= 300 ? 'bg-danger animate-pulse' : 'bg-black bg-opacity-80'}`}>
                {formatTime(timeLeftCounter)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div 
        className={`position-absolute bottom-0 start-0 end-0 p-4 transition-all duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`} 
        style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.85))', zIndex: 100 }} 
        onClick={e => e.stopPropagation()}
      >
        <div className="d-flex justify-content-center gap-4">

          {/* Speaker Button */}
          <button 
            onClick={toggleSpeaker} 
            className={`btn btn-lg rounded-circle shadow-lg ${speakerOn ? 'btn-success' : 'btn-danger'}`} 
            style={{ width: 70, height: 70 }}
          >
            <i className={`ti ${speakerOn ? 'ti-volume' : 'ti-volume-off'} fs-3`} />
          </button>

          {/* Mic Button – RED + CROSS when muted */}
          <button 
            onClick={toggleMic} 
            className={`btn btn-lg rounded-circle shadow-lg ${micMuted ? 'btn-danger' : 'btn-success'}`} 
            style={{ width: 70, height: 70 }}
          >
            <i className={`ti ${micMuted ? 'ti-microphone-off' : 'ti-microphone'} fs-3`} />
          </button>

          {/* Video Button – RED + CROSS when off */}
          {isVideoCall && (
            <button 
              onClick={toggleVideo} 
              className={`btn btn-lg rounded-circle shadow-lg ${videoOff ? 'btn-danger' : 'btn-success'}`} 
              style={{ width: 70, height: 70 }}
            >
              <i className={`ti ${videoOff ? 'ti-video-off' : 'ti-video'} fs-3`} />
            </button>
          )}

          {/* End Call */}
          <button 
            onClick={() => navigate(-1)} 
            className="btn btn-danger btn-lg rounded-circle shadow-lg" 
            style={{ width: 80, height: 80 }}
          >
            <i className="fa fa-phone fa-rotate-135 fs-2" />
          </button>

        </div>
      </div>

      {/* Local PiP Video */}
      {isVideoCall && (
        <div className="position-absolute bg-dark border border-white rounded-4 overflow-hidden shadow-2xl" style={{ width: 160, height: 220, bottom: 120, right: 20, zIndex: 200, cursor: 'move' }} draggable onDragStart={e => e.dataTransfer.setData("text", "")}>
          <div id="local-video-container" className="w-100 h-100" style={{ objectFit: 'cover' }} />
          {videoOff && (
            <div className="position-absolute inset-0 d-flex flex-column align-items-center justify-content-center bg-black bg-opacity-80 text-white">
              <img src={localImage || "/avatar.png"} alt="You" className="rounded-circle mb-2" style={{ width: 80, height: 80 }} />
              <small className="fw-bold">You</small>
              <small>Camera Off</small>
            </div>
          )}
          <div className="position-absolute bottom-0 start-0 end-0 p-2 text-center text-white" style={{ background: 'rgba(0,0,0,0.7)' }}>
            <small className="fw-bold">You</small>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default VideoCall;